import { useEffect, useRef, useState } from 'react';

import { GrayColors, Input, typographyStyle, Button, Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast, Typography } from '@revenuemarket/ui';
import { mixpanelTrack } from '@revenuemarket/utils/mixpanel';
import { Form, InputRef } from 'antd';
import { Rule } from 'antd/es/form';
import { motion } from 'framer-motion';
import * as yup from 'yup';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useSendPasscode, useVerifyPasscode } from '~hooks/queries/auth/query';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

export interface SignupEmailVerificationStepModel {
  email: string;
}

interface SignupEmailVerificationStepForm {
  email: string;
  passcode: string;
}

const SignupEmailVerificationStepScheme = yup.object({
  email: COMMON_YUP.email,
  passcode: yup
    .string()
    .required('인증번호를 입력해주세요.')
    .length(6, '인증번호 6자리를 모두 입력해주세요.'),
});

const getSchemeRule = (field: string): Rule => ({
  validator: async (_, value) => {
    await SignupEmailVerificationStepScheme.validateAt(field, { [field]: value });
  },
});

interface Props {
  model: SignupEmailVerificationStepModel;
  onBack?: () => void;
  onFinish: (values: SignupEmailVerificationStepForm) => void;
}

const SignupEmailVerificationStep = ({ model, onBack, onFinish: onFinishProp }: Props) => {
  const [form] = Form.useForm<SignupEmailVerificationStepForm>();
  const { mutateAsync: verifyPasscode } = useVerifyPasscode();
  const { mutateAsync: sendPasscode } = useSendPasscode();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<InputRef | null>(null);

  const onFinish = async (values: SignupEmailVerificationStepForm) => {
    setIsLoading(true);
    mixpanelTrack(MIXPANEL_EVENT.USER.INPUT_EMAIL_PASSCODE);
    const response = await verifyPasscode(values);
    if (response.Data?.isVerified) {
      mixpanelTrack(MIXPANEL_EVENT.USER.CONFIRM_EMAIL_PASSCODE, {
        response: 'success',
      });

      setTimeout(() => {
        setIsLoading(false);
        onFinishProp(values);
      }, 500);
    } else {
      setIsLoading(false);
      mixpanelTrack(MIXPANEL_EVENT.USER.CONFIRM_EMAIL_PASSCODE, {
        response: 'fail',
      });
      form.setFields([
        {
          name: 'passcode',
          errors: ['인증번호가 일치하지 않습니다.'],
        },
      ]);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Flex direction="column" gap={12} align="center">
      <div
        className={css`
          width: 440px;
          @media (max-width: 768px) {
            width: 100%;
            max-width: 440px;
          }
        `}
      >
        <Flex direction="column" gap={8}>
          <Typography
            variant="body1"
            color={GrayColors.gray900}
            style={{ ...typographyStyle['24_600'] }}
          >
            메일로 온 인증번호를 입력해주세요
          </Typography>

          <Typography
            variant="body1"
            color={GrayColors.gray900}
            style={{ ...typographyStyle['16_400'], marginBottom: 16 }}
          >
            <span style={{ ...typographyStyle['16_600'] }}>{model?.email}</span>에
            <br />
            전송된 메일이 보이지 않으면, 스팸함이나 프로모션함도 확인해 주세요.
          </Typography>
        </Flex>

        <Flex direction="column" gap={12}>
          <Form
            form={form}
            initialValues={{ ...model, passcode: '' }}
            onValuesChange={(_, values) => {
              if (values.passcode?.length === 6) {
                onFinish(values);
              }
            }}
            className={css`
              &&& .ant-form-item-explain-error {
                font-size: 14px;
              }
            `}
          >
            <Form.Item name="email" rules={[getSchemeRule('email')]} style={{ display: 'none' }}>
              <Input disabled size="large" type="email" placeholder="회사 이메일" />
            </Form.Item>

            <motion.div
              key="passcode"
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
            >
              <Form.Item name="passcode" rules={[getSchemeRule('passcode')]} style={{ margin: 0 }}>
                <Input
                  inputSize="xlarge"
                  placeholder="인증번호를 입력해주세요"
                  ref={inputRef}
                  maxLength={6}
                  inputStatus={isLoading ? 'loading' : undefined}
                />
              </Form.Item>
            </motion.div>
          </Form>

          <Flex align="center" gap={8}>
            <Typography
              variant="body1"
              color={GrayColors.gray600}
              style={{
                ...typographyStyle['14_400'],
              }}
            >
              메일을 받지 못했나요?
            </Typography>

            <Button
              buttonSize="default"
              buttonType="transparent"
              onClick={async () => {
                form.setFieldsValue({
                  passcode: '',
                });
                const response = await sendPasscode(model);
                if (response.Status === ResponseStatus.SUCCESS) {
                  Toast.success('인증번호를 다시 발송했습니다.');
                } else {
                  Toast.error('인증번호를 발송하는 데 실패하였습니다.');
                }
              }}
            >
              다시 전송하기
            </Button>
          </Flex>
        </Flex>
      </div>
    </Flex>
  );
};

export default SignupEmailVerificationStep;
